import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/sections";
import Lightbox from "@/components/Lightbox";
import { ArrowUpRight } from "lucide-react";

const ART_IMG = "https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?auto=format&fit=crop&w=1400&q=80";

const FALLBACK_RECENT_CLASSES = [
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/jic2zgt0_5.jpg",              caption: "Watercolour session · Sunday Art with David" },
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/k258kh78_1000104527.jpg",    caption: "Warli-inspired painting workshop · outdoor" },
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/utfmng7b_2.jpg",              caption: "Coaster painting · bird motifs" },
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/fqu87rk2_IMG20260705121437.webp", caption: "Painting birds · close study" },
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/7pjt2z0g_IMG20251129171635.webp", caption: "Painting circles · concentration" },
];

export default function ArtCentre() {
  const [events, setEvents] = useState([]);
  const [lbIdx, setLbIdx] = useState(-1);
  const [photos, setPhotos] = useState(FALLBACK_RECENT_CLASSES);
  useEffect(() => {
    api.get("/events", { params: { section: "art-centre" } })
      .then(r => setEvents(r.data)).catch(() => {});
    api.get("/page-galleries/art-centre")
      .then(r => { if (r.data?.photos?.length) setPhotos(r.data.photos); })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="art-centre-page">
      <section className="grid lg:grid-cols-12 gap-0 min-h-[80vh]">
        <div className="lg:col-span-6 order-2 lg:order-1 px-6 md:px-12 lg:px-20 py-16 flex flex-col justify-center">
          <div className="overline">02 — Art Centre</div>
          <h1 className="display text-6xl md:text-8xl mt-6">A room <em className="italic text-terra">for making.</em></h1>
          <p className="mt-8 text-base text-muted2 max-w-md leading-relaxed">
            Year-round classes and short workshops for makers of every age and stage. We focus on
            patient teaching, generous time and good materials. Drop in for a single session, or sign up
            for one of our multi-week courses.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 items-center">
            <Link to="/events?section=art-centre" className="btn-primary" data-testid="centre-classes">Find a class</Link>
            <Link to="/contact" className="btn-ghost">Ask us</Link>
            <button
              type="button"
              onClick={() => setLbIdx(0)}
              className="inline-flex items-center gap-2 text-sm hover:text-terra transition group ml-1"
              data-testid="art-centre-recent-classes"
            >
              <span className="border-b border-charcoal group-hover:border-terra transition pb-0.5">Photos from recent classes</span>
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              <span className="overline" style={{ fontSize: "0.55rem" }}>{photos.length} photos</span>
            </button>
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2 h-[60vh] lg:h-auto">
          <img src={ART_IMG} alt="Art Centre" className="w-full h-full object-cover"/>
        </div>
      </section>

      <section className="bg-cream-2 py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="overline">What we teach</div>
          <h2 className="display text-4xl md:text-5xl mt-4">Programmes.</h2>
          {events.length === 0 && (
            <p className="text-sm text-muted2 mt-6 max-w-md">New classes will be listed here soon. Check back or drop us a line to be notified.</p>
          )}
          <div className="numbered mt-12 grid md:grid-cols-2 gap-10">
            {events.map((c) => (
              <Link
                to={`/events/${c.slug}`}
                key={c.id}
                className="flex items-start gap-5 group"
                data-testid={`art-centre-programme-${c.slug}`}
              >
                <div className="grid grid-cols-[92px_1fr] gap-5 flex-1">
                  <div className="aspect-[4/5] overflow-hidden bg-cream border border-soft self-start">
                    {c.image_url ? (
                      <img src={c.image_url} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[9px] tracking-widest uppercase text-muted2">No image</div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-baseline gap-4 flex-wrap">
                      <div className="font-serif text-3xl tracking-tight group-hover:text-terra transition-colors">{c.title}</div>
                      {c.is_featured && (
                        <span className="px-1.5 py-0.5 text-[9px] tracking-widest uppercase" style={{background:"#D4A373",color:"#1C1C19"}} data-testid={`art-centre-highlight-${c.slug}`}>Highlight</span>
                      )}
                      {c.price_inr > 0 && (
                        <div className="font-serif text-xl text-terra">{formatINR(c.price_inr)}</div>
                      )}
                    </div>
                    {(c.recurrence_label || c.short_description) && (
                      <p className="text-sm text-muted2 mt-3 max-w-md leading-relaxed">
                        {c.recurrence_label && <span className="text-charcoal">{c.recurrence_label}. </span>}
                        {c.short_description}
                      </p>
                    )}
                    <div className="mt-3 overline inline-flex items-center gap-1 text-terra opacity-0 group-hover:opacity-100 transition-opacity">
                      Book this class <ArrowUpRight size={11} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {lbIdx >= 0 && (
        <Lightbox
          images={photos}
          index={lbIdx}
          onIndex={setLbIdx}
          onClose={() => setLbIdx(-1)}
        />
      )}
    </div>
  );
}
